import React, {useEffect, useRef, useState} from 'react'
import {Box, CircularProgress} from '@mui/material'
import {Document, Page, pdfjs} from 'react-pdf'
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
import pdfWorkerSrc from '!!file-loader!pdfjs-dist/build/pdf.worker.min.js'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

// Disable pdf.js eval-based font rendering: with eval enabled a crafted PDF can run arbitrary
// JS (CVE-2024-4367, fixed upstream only in pdfjs-dist 4.2.67). Module-level so the reference
// stays stable — react-pdf reloads the document whenever `options` changes.
const PDF_OPTIONS = {isEvalSupported: false}

interface PdfPreviewProps {
  filePath: string
  fileName: string
  pageNumber: number
  onDocumentLoad: (numPages: number) => void
  onDocumentError: () => void
}

export default function PdfPreview({
  filePath,
  fileName,
  pageNumber,
  onDocumentLoad,
  onDocumentError,
}: PdfPreviewProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pageWidth, setPageWidth] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setPageWidth(width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <Box aria-label={fileName} ref={containerRef} sx={{width: '100%'}}>
      <Document
        file={filePath}
        loading={
          <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
            <CircularProgress size={24} />
          </Box>
        }
        onLoadError={onDocumentError}
        onLoadSuccess={(pdf) => onDocumentLoad(pdf.numPages)}
        options={PDF_OPTIONS}
      >
        {pageWidth > 0 && (
          <Page
            pageNumber={pageNumber}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            width={pageWidth}
          />
        )}
      </Document>
    </Box>
  )
}
